import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LogsTable = ({ 
  logs, 
  onSort, 
  sortConfig, 
  selectedLogs, 
  onSelectLog, 
  onSelectAll,
  onLogDetails 
}) => {
  const [expandedLog, setExpandedLog] = useState(null);

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp)?.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const getActionBadge = (action) => {
    const isGranted = action === 'Granted';
    return (
      <span className={`
        inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
        ${isGranted 
          ? 'bg-success/10 text-success border border-success/20' :'bg-error/10 text-error border border-error/20'
        }
      `}>
        <Icon 
          name={isGranted ? "CheckCircle" : "XCircle"} 
          size={12} 
          className="mr-1" 
        />
        {action}
      </span>
    );
  };

  const getAnomalyBadge = (isAnomaly, anomalyReason) => {
    if (!isAnomaly) return null;
    
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-warning/10 text-warning border border-warning/20 security-pulse">
        <Icon name="AlertTriangle" size={12} className="mr-1" />
        Anomaly
      </span>
    );
  };

  const getSortIcon = (column) => {
    if (sortConfig?.key !== column) {
      return <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" />;
    }
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ArrowUp" size={14} className="text-accent" />
      : <Icon name="ArrowDown" size={14} className="text-accent" />;
  };

  const handleSort = (column) => {
    onSort(column);
  };

  const toggleExpanded = (logId) => {
    setExpandedLog(expandedLog === logId ? null : logId);
  };

  const allSelected = logs?.length > 0 && selectedLogs?.length === logs?.length;
  const someSelected = selectedLogs?.length > 0 && selectedLogs?.length < logs?.length;

  return (
    <div className="bg-card border border-border rounded-lg security-shadow-card overflow-hidden">
      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={input => {
                    if (input) input.indeterminate = someSelected;
                  }}
                  onChange={onSelectAll}
                  className="w-4 h-4 text-accent bg-input border-border rounded focus:ring-accent focus:ring-2"
                />
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('timestamp')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-accent security-transition"
                >
                  <span>Timestamp</span>
                  {getSortIcon('timestamp')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('userId')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-accent security-transition"
                >
                  <span>User</span>
                  {getSortIcon('userId')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('doorId')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-accent security-transition"
                >
                  <span>Door</span>
                  {getSortIcon('doorId')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('action')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-accent security-transition"
                >
                  <span>Action</span>
                  {getSortIcon('action')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-sm font-medium text-foreground">Status</span>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-sm font-medium text-foreground">Details</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {logs?.map((log) => (
              <React.Fragment key={log?.id}>
                <tr className={`hover:bg-muted/30 security-transition ${selectedLogs?.includes(log?.id) ? 'bg-accent/5' : ''}`}>
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedLogs?.includes(log?.id)}
                      onChange={() => onSelectLog(log?.id)}
                      className="w-4 h-4 text-accent bg-input border-border rounded focus:ring-accent focus:ring-2"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-foreground font-mono">
                      {formatTimestamp(log?.timestamp)}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                        <Icon name="User" size={14} className="text-secondary-foreground" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground">{log?.userId}</div>
                        <div className="text-xs text-muted-foreground">{log?.userRole}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-foreground">{log?.doorId}</div>
                    <div className="text-xs text-muted-foreground">{log?.doorName}</div>
                  </td>
                  <td className="px-4 py-3">
                    {getActionBadge(log?.action)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-2">
                      {getAnomalyBadge(log?.isAnomaly, log?.anomalyReason)}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleExpanded(log?.id)}
                      iconName={expandedLog === log?.id ? "ChevronUp" : "ChevronDown"}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      {expandedLog === log?.id ? 'Hide' : 'Show'}
                    </Button>
                  </td>
                </tr>
                {expandedLog === log?.id && (
                  <tr>
                    <td colSpan="7" className="px-4 py-4 bg-muted/20">
                      <div className="space-y-3">
                        <h4 className="text-sm font-semibold text-foreground">Additional Details</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">IP Address:</span>
                            <span className="ml-2 text-foreground font-mono">{log?.ipAddress}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Device:</span>
                            <span className="ml-2 text-foreground">{log?.device}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Method:</span>
                            <span className="ml-2 text-foreground">{log?.method}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Duration:</span>
                            <span className="ml-2 text-foreground">{log?.duration}ms</span>
                          </div>
                          {log?.isAnomaly && (
                            <div className="md:col-span-2">
                              <span className="text-muted-foreground">Anomaly Reason:</span>
                              <span className="ml-2 text-warning">{log?.anomalyReason}</span>
                            </div>
                          )}
                          {log?.notes && (
                            <div className="md:col-span-2">
                              <span className="text-muted-foreground">Notes:</span>
                              <span className="ml-2 text-foreground">{log?.notes}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Card View */}
      <div className="md:hidden divide-y divide-border">
        {logs?.map((log) => (
          <div key={log?.id} className={`p-4 ${selectedLogs?.includes(log?.id) ? 'bg-accent/5' : ''}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={selectedLogs?.includes(log?.id)}
                  onChange={() => onSelectLog(log?.id)}
                  className="w-4 h-4 text-accent bg-input border-border rounded focus:ring-accent focus:ring-2"
                />
                <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                  <Icon name="User" size={14} className="text-secondary-foreground" />
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground">{log?.userId}</div>
                  <div className="text-xs text-muted-foreground">{log?.userRole}</div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {getAnomalyBadge(log?.isAnomaly, log?.anomalyReason)}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleExpanded(log?.id)}
                  iconName={expandedLog === log?.id ? "ChevronUp" : "ChevronDown"}
                  className="text-muted-foreground"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Door:</span>
                <span className="text-sm text-foreground">{log?.doorId} - {log?.doorName}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Action:</span>
                {getActionBadge(log?.action)}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Time:</span>
                <span className="text-sm text-foreground font-mono">
                  {formatTimestamp(log?.timestamp)}
                </span>
              </div>
            </div>

            {expandedLog === log?.id && (
              <div className="mt-4 pt-4 border-t border-border space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">IP Address:</span>
                  <span className="text-sm text-foreground font-mono">{log?.ipAddress}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Device:</span>
                  <span className="text-sm text-foreground">{log?.device}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Method:</span>
                  <span className="text-sm text-foreground">{log?.method}</span>
                </div>
                {log?.isAnomaly && (
                  <div className="pt-2">
                    <span className="text-xs text-muted-foreground">Anomaly:</span>
                    <p className="text-sm text-warning mt-1">{log?.anomalyReason}</p>
                  </div>
                )}
                {log?.notes && (
                  <div className="pt-2">
                    <span className="text-xs text-muted-foreground">Notes:</span>
                    <p className="text-sm text-foreground mt-1">{log?.notes}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Empty State */}
      {logs?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Access Logs Found</h3>
          <p className="text-muted-foreground">
            No logs match your current filter criteria. Try adjusting your filters.
          </p>
        </div>
      )}
    </div>
  );
};

export default LogsTable;